import * as React from 'react'
import './style/domain.scss'
import { classnames } from '@halo/_utils/classnames'
import { PageHeader } from '@pushly/aqe/lib/components'
import { AppState } from '@halo/stores/app.state'
import { AppService } from '@halo/services/app.service'
import { Container } from 'typescript-ioc/es5'
import { Tabs, Breadcrumb } from 'antd'
import { DomainDataForm } from '@halo/components/domain-data-form/domain-data-form'
import { noop } from '@pushly/aqe/lib/utils/noop'
import TabbedView from '@halo/components/tabbed-view/tabbed-view'
import { AbilityAction } from '@halo/enums/ability-action.enum'
import { asCaslSubject } from '@halo/stores/ability.state'
import { SubjectEntity } from '@halo/enums/ability-entity.enum'
import { DomainDto } from '@halo/dtos/domain.dto'
import { DomainService } from '@halo/services/domain.service'
import { Loading3QuartersOutlined } from '@ant-design/icons'
import { ORGANIZATIONS_ROUTE } from '@halo/portals/post-auth/accounts'
import { DOMAINS_ROUTE } from '@halo/portals/post-auth/domains'
import { RouteComponentProps } from 'react-router-dom'
import { AccountDto } from '@halo/dtos/account.dto'
import { ErrorPage } from '@halo/components/error-page/error-page'
import { FeedsDataView } from '@halo/portals/post-auth/domains/feeds/feeds-data-view'

interface IProps
    extends RouteComponentProps<
        { accountId?: string; domainId: string },
        any,
        { account?: AccountDto; domain?: DomainDto }
    > {}

interface IState {
    loading: boolean
    domain?: DomainDto
}

export class Domain extends React.Component<IProps, IState> {
    protected appState: AppState
    protected appService: AppService
    protected domainService: DomainService

    public constructor(props: IProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.domainService = Container.get(DomainService)

        this.state = {
            loading: !props.location.state?.domain,
            domain: props.location.state?.domain,
        }
    }

    public componentDidMount() {
        if (!this.state.domain) {
            this.loadRequestedDomain()
        }
    }

    public render() {
        const canAccessFeeds = this.appState.abilityStore.can(
            AbilityAction.UPDATE,
            asCaslSubject(SubjectEntity.FEED, {
                domainId: this.requestedDomainId,
            }),
        )

        return (
            <div className={classnames('page', 'single-domain')}>
                <div className={classnames('page-wrapper')}>
                    <PageHeader
                        title="Domain"
                        onTitleSet={noop}
                        append={
                            !this.state.domain ? (
                                <Loading3QuartersOutlined spin={this.state.loading} />
                            ) : (
                                <Breadcrumb>
                                    {!!this.requestedAccountId && (
                                        <React.Fragment>
                                            <Breadcrumb.Item
                                                href={ORGANIZATIONS_ROUTE}
                                                onClick={this.handleBreadcrumbClick}
                                            >
                                                Organizations
                                            </Breadcrumb.Item>
                                            <Breadcrumb.Item
                                                href={`${ORGANIZATIONS_ROUTE}/${this.requestedAccountId}`}
                                                onClick={this.handleBreadcrumbClick}
                                            >
                                                {this.state.domain.account_name}
                                            </Breadcrumb.Item>
                                        </React.Fragment>
                                    )}
                                    <Breadcrumb.Item
                                        href={
                                            this.requestedAccountId
                                                ? `${ORGANIZATIONS_ROUTE}/${this.requestedAccountId}#domains`
                                                : DOMAINS_ROUTE
                                        }
                                        onClick={this.handleBreadcrumbClick}
                                    >
                                        Domains
                                    </Breadcrumb.Item>
                                    <Breadcrumb.Item>{this.state.domain.display_name}</Breadcrumb.Item>
                                </Breadcrumb>
                            )
                        }
                    />

                    <TabbedView
                        className={classnames(null, {
                            ['feeds-tab-hidden']: !canAccessFeeds,
                        })}
                        availablePathKeys={['feeds']}
                        defaultActiveKey="manage"
                        size="small"
                        animated={false}
                    >
                        <Tabs.TabPane key="manage" tab="Management">
                            <DomainDataForm domainId={this.requestedDomainId} />
                        </Tabs.TabPane>

                        <Tabs.TabPane key="feeds" tab="Feeds" disabled={!canAccessFeeds}>
                            {!canAccessFeeds ? (
                                <ErrorPage errorCode={403} />
                            ) : (
                                this.state.domain && (
                                    <FeedsDataView domainId={this.requestedDomainId} domain={this.state.domain} />
                                )
                            )}
                        </Tabs.TabPane>
                    </TabbedView>
                </div>
            </div>
        )
    }

    protected get requestedDomainId(): number {
        return parseInt(this.props.match.params.domainId, 0)
    }

    protected get requestedAccountId(): number {
        return parseInt(this.props.match.params.accountId, 0)
    }

    protected handleBreadcrumbClick = (ev: React.MouseEvent<HTMLAnchorElement>) => {
        if (!ev.metaKey) {
            ev.preventDefault()
            this.appService.route(ev.currentTarget.getAttribute('href'), {
                account: this.props.location.state?.account,
            })
        }
    }

    protected async loadRequestedDomain() {
        this.setState({ loading: true })

        try {
            const { ok, data } = await this.domainService.fetchById(this.requestedDomainId)
            if (ok) {
                this.setState({ domain: data })
            }
        } catch {}

        this.setState({ loading: false })
    }
}
